import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import SCUIGenericExcelUpload from '../../atomic/molecules/SCUIGenericExcelUpload/SCUIGenericExcelUpload';
import { SCUIContent } from '../../atomic/organisms';
import OrderCommand from '../../services/command/order/OrderCommand.services';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import {
    BULK_SHIPMENT_MAX_ROWS,
    BulkShipmentUpdateType,
    countBulkShipmentDataRows,
    downloadBulkShipmentTemplate,
} from './bulkShipmentTemplateHelper';
import { MPBSU } from './mpBulkShipmentUpdate.classes';

export const MPBulkShipmentUpdate = (): React.JSX.Element => {
    const { t } = useTranslation();
    const [updateType, setUpdateType] = useState<BulkShipmentUpdateType>('ShippingConfirmed');
    const [isLoading, setIsLoading] = useState(false);
    const [uploadResponse, setUploadResponse] = useState<any>(null);
    const [uploadResetKey, setUploadResetKey] = useState(0);

    let columnRule = t('mpBulkShipmentUpdate.rules.selectTypeForColumns');
    if (updateType === 'ShippingConfirmed') {
        columnRule = t('mpBulkShipmentUpdate.rules.shippingConfirmedColumns');
    } else if (updateType === 'Delivered') {
        columnRule = t('mpBulkShipmentUpdate.rules.deliveredColumns');
    }

    const rules = useMemo(
        () => [
            t('mpBulkShipmentUpdate.rules.maxRows', { max: BULK_SHIPMENT_MAX_ROWS }),
            t('mpBulkShipmentUpdate.rules.yellowRowsHint', { max: BULK_SHIPMENT_MAX_ROWS }),
            t('mpBulkShipmentUpdate.rules.shippingListNoOnly'),
            columnRule,
        ],
        [t, columnRule]
    );

    const handleTypeChange = (event: SelectChangeEvent) => {
        setUpdateType(event.target.value as BulkShipmentUpdateType);
        setUploadResponse(null);
        setUploadResetKey((k) => k + 1);
    };

    const handleDownloadTemplate = async () => {
        if (!updateType) return;
        await downloadBulkShipmentTemplate(updateType);
    };

    const onUpload = async (file: File) => {
        if (!updateType) {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('mpBulkShipmentUpdate.selectUpdateTypeFirst'),
                confirmButtonText: t('general.ok'),
            });
            return;
        }

        setIsLoading(true);
        try {
            const rowCount = await countBulkShipmentDataRows(file);
            if (rowCount > BULK_SHIPMENT_MAX_ROWS) {
                Swal.fire({
                    icon: 'warning',
                    title: t('general.warning'),
                    text: t('mpBulkShipmentUpdate.maxRowsExceeded', { max: BULK_SHIPMENT_MAX_ROWS }),
                    confirmButtonText: t('general.ok'),
                });
                return;
            }

            if (rowCount === 0) {
                Swal.fire({
                    icon: 'warning',
                    title: t('general.warning'),
                    text: t('mpBulkShipmentUpdate.emptyExcel'),
                    confirmButtonText: t('general.ok'),
                });
                return;
            }

            setUploadResponse(null);
            const formData = new FormData();
            formData.append('excel', file, file.name);
            formData.append('updateType', updateType);

            const response = await OrderCommand.BulkUpdateShipmentStateExcel(formData);
            setUploadResponse(response);

            if (response?.message === 'ERROR_MAX_ROWS_EXCEEDED' || response?.error === 'ERROR_MAX_ROWS_EXCEEDED') {
                Swal.fire({
                    icon: 'warning',
                    title: t('general.warning'),
                    text: t('mpBulkShipmentUpdate.maxRowsExceeded', { max: BULK_SHIPMENT_MAX_ROWS }),
                    confirmButtonText: t('general.ok'),
                });
                return;
            }

            if (response?.isSuccess === false) {
                await Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: response?.message || t('general.anErrorOccured'),
                    confirmButtonText: t('general.ok'),
                });
                setUploadResetKey((k) => k + 1);
                return;
            }

            const successCount = response?.successCount ?? 0;
            const failedCount = response?.failedCount ?? 0;
            setUploadResetKey((k) => k + 1);
            Swal.fire({
                icon: failedCount > 0 ? 'warning' : 'success',
                title: t('general.success'),
                html: `${t('mpBulkShipmentUpdate.successCount')}: ${successCount}<br/>${t('mpBulkShipmentUpdate.failedCount')}: ${failedCount}`,
                confirmButtonText: t('general.ok'),
            });
        } catch {
            setUploadResponse(null);
            await Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.anErrorOccured'),
                confirmButtonText: t('general.ok'),
            });
            setUploadResetKey((k) => k + 1);
        } finally {
            setIsLoading(false);
        }
    };

    const failedItems = Array.isArray(uploadResponse?.failedItems) ? uploadResponse.failedItems : [];

    return (
        <SCUIContent className={MPBSU.page}>
            <Box className={MPBSU.breadcrumbSection}>
                <SCUIBreadCrumb
                    title={t('mpBulkShipmentUpdate.title')}
                    componentLink={PageUrl.mpBulkShipmentUpdate.url}
                />
            </Box>
            <SCUILoadingLinear display={isLoading} />

            <Paper className={MPBSU.shellPaper} sx={{ p: 3, mt: 2 }}>
                <Box className={MPBSU.content} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6">{t('mpBulkShipmentUpdate.subtitle')}</Typography>

                    <FormControl className={MPBSU.typeField} sx={{ minWidth: 280 }} size="small">
                        <InputLabel id="mp-bulk-shipment-update-type-label">
                            {t('mpBulkShipmentUpdate.updateType')}
                        </InputLabel>
                        <Select
                            className={MPBSU.inputSelectUpdateType}
                            labelId="mp-bulk-shipment-update-type-label"
                            label={t('mpBulkShipmentUpdate.updateType')}
                            value={updateType}
                            onChange={handleTypeChange}
                        >
                            <MenuItem value="ShippingConfirmed">
                                {t('mpBulkShipmentUpdate.types.shippingConfirmed')}
                            </MenuItem>
                            <MenuItem value="Delivered">{t('mpBulkShipmentUpdate.types.delivered')}</MenuItem>
                        </Select>
                    </FormControl>

                    <Paper
                        className={MPBSU.rulesPaper}
                        elevation={0}
                        sx={{
                            p: 2.5,
                            backgroundColor: '#FFF8E1',
                            border: '1px solid #FFD54F',
                            borderLeft: '5px solid #FFC107',
                            borderRadius: '8px',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                mb: 1.25,
                                fontWeight: 700,
                                fontSize: '15px',
                                color: '#E65100',
                                letterSpacing: '0.01em',
                            }}
                        >
                            {t('mpBulkShipmentUpdate.excelRulesTitle')}
                        </Typography>
                        <Box
                            component="ul"
                            sx={{
                                m: 0,
                                pl: 2.5,
                                '& li': {
                                    mb: 1,
                                    fontSize: '13px',
                                    lineHeight: 1.6,
                                    fontWeight: 500,
                                    color: '#5D4037',
                                },
                                '& li::marker': {
                                    color: '#FF8F00',
                                    fontWeight: 700,
                                },
                            }}
                        >
                            {rules.map((rule) => (
                                <Typography component="li" key={rule} variant="body2">
                                    {rule}
                                </Typography>
                            ))}
                        </Box>
                    </Paper>

                    <Box className={MPBSU.downloadWrap} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <SCUIButton
                            className={MPBSU.inputButtonDownloadTemplate}
                            type="secondary"
                            text={t('mpBulkShipmentUpdate.downloadExample')}
                            disabled={!updateType || isLoading}
                            onClick={handleDownloadTemplate}
                        />
                    </Box>

                    <Box className={MPBSU.uploadWrap}>
                        <SCUIGenericExcelUpload
                            key={uploadResetKey}
                            onUpload={onUpload}
                            showExample={false}
                            disabled={!updateType || isLoading}
                            loading={isLoading}
                            title={t('mpBulkShipmentUpdate.uploadTitle')}
                            description={t('mpBulkShipmentUpdate.uploadDescription')}
                        />
                    </Box>

                    {uploadResponse && (
                        <Box className={MPBSU.statusRow} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label={`${t('mpBulkShipmentUpdate.successCount')}: ${uploadResponse.successCount ?? 0}`}
                                sx={{ backgroundColor: '#4caf50', color: 'white' }}
                            />
                            <Chip
                                label={`${t('mpBulkShipmentUpdate.failedCount')}: ${uploadResponse.failedCount ?? 0}`}
                                sx={{ backgroundColor: '#f44336', color: 'white' }}
                            />
                            {uploadResponse.jobId && (
                                <Chip
                                    label={`${t('mpBulkShipmentUpdate.jobId')}: ${uploadResponse.jobId}`}
                                    sx={{ backgroundColor: '#2196f3', color: 'white' }}
                                />
                            )}
                        </Box>
                    )}

                    {failedItems.length > 0 && (
                        <Box className={MPBSU.failedSection} sx={{ mt: 1 }}>
                            <Typography className={MPBSU.failedTitle} sx={{ color: '#f44336', fontWeight: 'bold', mb: 1 }}>
                                {t('mpBulkShipmentUpdate.failedRecords')}
                            </Typography>
                            <Box className={MPBSU.failedGridWrap}>
                                <SCUICustomDataGrid
                                    rows={failedItems.map((item: any, index: number) => ({
                                        id: `${item.rowNumber}-${index}`,
                                        uniqueKey: `${item.rowNumber}-${index}`,
                                        rowNumber: item.rowNumber,
                                        shippingListNo: item.shippingListNo || '-',
                                        errorMessage: item.errorMessage,
                                    }))}
                                    getRowId={(row: any) => row.uniqueKey}
                                    page={0}
                                    pageSize={5}
                                    columns={[
                                        {
                                            field: 'rowNumber',
                                            headerName: t('mpBulkShipmentUpdate.rowNumber'),
                                            flex: 0.5,
                                        },
                                        {
                                            field: 'shippingListNo',
                                            headerName: t('excel.shippingListNo'),
                                            flex: 1,
                                        },
                                        {
                                            field: 'errorMessage',
                                            headerName: t('mpBulkShipmentUpdate.errorMessage'),
                                            flex: 2,
                                        },
                                    ]}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>
        </SCUIContent>
    );
};

export default MPBulkShipmentUpdate;
